import type {PullRequestPickerPullRequest} from '@github-ui/item-picker/PullRequestAndBranchPicker'
import {
  createMutationId,
  dispatchMutationLifecycleEvent,
  MutationLifecycleEvent,
  type ObjectPropertiesPatch,
  UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT,
} from '@github-ui/mutation-lifecycle-events'
import {commitMutation, type Environment, graphql} from 'react-relay'

import type {
  linkPullRequestsMutation,
  linkPullRequestsMutation$data,
  linkPullRequestsMutation$rawResponse,
} from './__generated__/linkPullRequestsMutation.graphql'

type LinkPullRequestsMutationParams = {
  environment: Environment
  input: {baseIssueOrPullRequestId: string; linkingIds: string[]}
  onError: (error: Error) => void
  onCompleted?: () => void
  linkingPrs: PullRequestPickerPullRequest[]
}

function toObjectPropertiesPatch(
  data: linkPullRequestsMutation$data | linkPullRequestsMutation$rawResponse,
  baseIssueOrPullRequestId: string,
): ObjectPropertiesPatch | undefined {
  const baseIssueOrPullRequest = data.linkIssueOrPullRequest?.baseIssueOrPullRequest
  if (!baseIssueOrPullRequest || !('closedByPullRequestsReferences' in baseIssueOrPullRequest)) return undefined

  return {
    id: baseIssueOrPullRequestId,
    values: {closedByPullRequestsReferences: baseIssueOrPullRequest.closedByPullRequestsReferences},
  }
}

export function linkPullRequestsMutation({
  environment,
  input: {baseIssueOrPullRequestId, linkingIds},
  onError,
  onCompleted,
  linkingPrs,
}: LinkPullRequestsMutationParams) {
  const optimisticResponse: linkPullRequestsMutation$rawResponse = {
    linkIssueOrPullRequest: {
      baseIssueOrPullRequest: {
        __typename: 'Issue',
        id: baseIssueOrPullRequestId,
        __isNode: 'Issue',
        closedByPullRequestsReferences: {
          nodes: (linkingPrs || []).map(pr => {
            const {url, number, title, isDraft, isInMergeQueue, state, repository, id: pullRequestId, createdAt} = pr
            const {id: repoId, name, nameWithOwner, owner} = repository
            const {login, __typename, id: ownerId} = owner

            return {
              id: pullRequestId,
              createdAt,
              isDraft,
              isInMergeQueue,
              number,
              url,
              title,
              state,
              __typename: 'PullRequest',
              repository: {
                id: repoId,
                name,
                nameWithOwner,
                owner: {
                  id: ownerId,
                  login,
                  __typename,
                },
              },
            }
          }),
        },
      },
    },
  }

  const optimisticPatch = toObjectPropertiesPatch(optimisticResponse, baseIssueOrPullRequestId)
  const optimisticEvent =
    optimisticPatch &&
    new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'optimistic', data: optimisticPatch})
  const mutationId = optimisticEvent?.detail.mutationId ?? createMutationId()

  const disposable = commitMutation<linkPullRequestsMutation>(environment, {
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    mutation: graphql`
      mutation linkPullRequestsMutation($baseIssueOrPullRequestId: ID!, $linkingIds: [ID!]!) @raw_response_type {
        linkIssueOrPullRequest(input: {baseIssueOrPullRequestId: $baseIssueOrPullRequestId, linkingIds: $linkingIds}) {
          baseIssueOrPullRequest {
            ... on Issue {
              closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
                nodes {
                  ... on PullRequest {
                    id
                    url
                    number
                    title
                    state
                    isDraft
                    isInMergeQueue
                    createdAt
                    repository {
                      id
                      name
                      nameWithOwner
                      owner {
                        id
                        login
                        __typename
                      }
                    }
                    __typename
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {baseIssueOrPullRequestId, linkingIds},
    optimisticResponse,
    onError: error => {
      dispatchMutationLifecycleEvent(
        new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {phase: 'error', mutationId, error}),
      )
      onError(error)
    },
    onCompleted: response => {
      const patch = toObjectPropertiesPatch(response, baseIssueOrPullRequestId)
      if (patch) {
        dispatchMutationLifecycleEvent(
          new MutationLifecycleEvent(UPDATE_OBJECT_PROPERTIES_MUTATION_EVENT, {
            phase: 'completed',
            mutationId,
            data: patch,
          }),
        )
      }
      onCompleted?.()
    },
  })

  if (optimisticEvent) {
    dispatchMutationLifecycleEvent(optimisticEvent)
  }

  return disposable
}
